// Gallery Edition: rigid stretched canvas, recessed shadow gap and mitered wood frame.
// The original portrait is reused at full resolution; no baked room or floor shadow.
async function initializeGallery(root) {
  if (root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const stage = root.querySelector('.pp-stage'), status = root.querySelector('.pp-status');
  const shadow = root.querySelector('.pp-shadow');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const compact = matchMedia('(max-width: 749px)'), coarse = matchMedia('(pointer: coarse)');
  const life = new AbortController(), geometries = [], materials = [], textures = [];
  let disposed = false, renderer, resizeObserver, visibilityObserver;
  let frame = 0, lastTime = 0, visible = true, pointerInside = false, hasFocus = false;
  let demoStart = null, playedMobile = false, px = 0, py = 0, rx = 0, ry = 0, lift = 0;
  const motionScale = 1.30, tilt = 3.84 * motionScale, inertia = .23;
  function dispose() {
    if (disposed) return;
    disposed = true; life.abort(); cancelAnimationFrame(frame);
    resizeObserver?.disconnect(); visibilityObserver?.disconnect();
    geometries.forEach(g => g.dispose()); materials.forEach(m => m.dispose()); textures.forEach(t => t.dispose());
    renderer?.dispose(); renderer?.domElement.remove();
    delete root.dataset.initialized; delete root.dataset.ready;
  }
  root.disposePreview = dispose;
  try {
    const THREE = await import('https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js');
    if (disposed || !root.isConnected) { dispose(); return; }
    const image = new Image(); image.crossOrigin = 'anonymous'; image.src = root.querySelector('.pp-gallery-image').src;
    await image.decode();
    if (disposed || !root.isConnected) { dispose(); return; }
    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
    renderer.setClearColor(0x000000, 0); renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.domElement.setAttribute('aria-hidden', 'true'); stage.append(renderer.domElement);
    const artwork = new THREE.Texture(image);
    artwork.colorSpace = THREE.SRGBColorSpace; artwork.minFilter = THREE.LinearMipmapLinearFilter;
    artwork.magFilter = THREE.LinearFilter; artwork.anisotropy = Math.min(16, renderer.capabilities.getMaxAnisotropy());
    artwork.generateMipmaps = true; artwork.needsUpdate = true; textures.push(artwork);
    const scene = new THREE.Scene();
    const fov = 2 * Math.atan(36 / (2 * 105)) * 180 / Math.PI;
    const camera = new THREE.PerspectiveCamera(fov, 1, .01, 20);
    const distance = 2.1, yaw = THREE.MathUtils.degToRad(5.5), elevation = THREE.MathUtils.degToRad(2);
    camera.position.set(distance * Math.sin(yaw) * Math.cos(elevation), -.008 + distance * Math.sin(elevation), distance * Math.cos(yaw) * Math.cos(elevation));
    camera.lookAt(0, -.008, 0);
    const model = new THREE.Group(); model.name = 'Gallery Edition · canvas in natural floating frame'; scene.add(model);
    const restingYaw = THREE.MathUtils.degToRad(-4.5);

    // Procedural material maps only: subtle longitudinal grain, not an altered portrait.
    function woodMap(horizontal = false) {
      const w = 128, h = 512, data = new Uint8Array(w * h * 4);
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const u = x / w, v = y / h;
        const wave = Math.sin(u * 132 + Math.sin(v * 10) * .8 + Math.sin(v * 26) * .18);
        const fine = Math.sin(u * 421 + Math.sin(v * 17) * .45);
        const pores = Math.max(0, Math.sin(u * 287 + Math.sin(v * 47))) ** 22;
        const shade = wave * 5 + fine * 2 - pores * 10;
        const i = (y * w + x) * 4;
        data[i] = 194 + shade; data[i + 1] = 163 + shade; data[i + 2] = 119 + shade; data[i + 3] = 255;
      }
      const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
      tex.colorSpace = THREE.SRGBColorSpace; tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.magFilter = THREE.LinearFilter; tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.generateMipmaps = true; tex.anisotropy = artwork.anisotropy;
      // Extrude UVs are in model units. Rotate horizontal rails' grain along their length.
      tex.repeat.set(7, 2); if (horizontal) { tex.rotation = Math.PI / 2; tex.center.set(.5, .5); }
      tex.needsUpdate = true; textures.push(tex); return tex;
    }
    const woodVertical = new THREE.MeshStandardMaterial({ map: woodMap(), roughness: .72, metalness: 0 });
    const woodHorizontal = new THREE.MeshStandardMaterial({ map: woodMap(true), roughness: .72, metalness: 0 });
    const recess = new THREE.MeshStandardMaterial({ color: 0x17130e, roughness: 1 });
    const linenSide = new THREE.MeshStandardMaterial({ color: 0xe4ddcb, roughness: 1 });
    materials.push(woodVertical, woodHorizontal, recess, linenSide);

    const threadSize = 128, weaveData = new Uint8Array(threadSize * threadSize * 4);
    for (let y = 0; y < threadSize; y++) for (let x = 0; x < threadSize; x++) {
      const value = 128 + 30 * Math.sin(x * Math.PI / 4) * Math.cos(y * Math.PI / 4);
      const i = (y * threadSize + x) * 4;
      weaveData[i] = weaveData[i + 1] = weaveData[i + 2] = value; weaveData[i + 3] = 255;
    }
    const weave = new THREE.DataTexture(weaveData, threadSize, threadSize, THREE.RGBAFormat);
    weave.wrapS = weave.wrapT = THREE.RepeatWrapping; weave.repeat.set(14, 17.5);
    weave.magFilter = THREE.LinearFilter; weave.minFilter = THREE.LinearMipmapLinearFilter;
    weave.generateMipmaps = true; weave.needsUpdate = true; textures.push(weave);
    const print = new THREE.MeshPhysicalMaterial({ map: artwork, color: 0xffffff, roughness: .98, metalness: 0, specularIntensity: .06, bumpMap: weave, bumpScale: .000008 });
    materials.push(print);

    function mesh(geometry, material, z = 0) {
      geometries.push(geometry); const item = new THREE.Mesh(geometry, material); item.position.z = z; model.add(item); return item;
    }
    // Illustration proportions: matched outer height, actual open gap and recessed canvas.
    // These model units are not customer-facing size specifications.
    const canvasH = .48, canvasW = canvasH * image.naturalWidth / image.naturalHeight;
    const gap = .009, rim = .011, depth = .042;
    const outerW = canvasW + 2 * (gap + rim), outerH = .52;
    const x = outerW / 2, y = outerH / 2, ix = x - rim, iy = y - rim;
    const railPoints = [
      [[-x, y], [x, y], [ix, iy], [-ix, iy]],
      [[x, y], [x, -y], [ix, -iy], [ix, iy]],
      [[x, -y], [-x, -y], [-ix, -iy], [ix, -iy]],
      [[-x, -y], [-x, y], [-ix, iy], [-ix, -iy]]
    ];
    railPoints.forEach((points, i) => {
      const shape = new THREE.Shape(); shape.moveTo(...points[0]); points.slice(1).forEach(p => shape.lineTo(...p)); shape.closePath();
      const geo = new THREE.ExtrudeGeometry(shape, { depth: depth - .001, bevelEnabled: true, bevelSize: .00025, bevelThickness: .0005, bevelSegments: 2, steps: 1 });
      geo.translate(0, 0, -depth / 2);
      mesh(geo, i % 2 ? woodVertical : woodHorizontal).name = 'Mitered natural wood rail';
    });
    mesh(new THREE.BoxGeometry(outerW - rim, outerH - rim, .002), recess, -.019).name = 'Dark base of the open shadow gap';
    mesh(new THREE.BoxGeometry(canvasW, canvasH, .027), linenSide, .002).name = 'Stretched canvas body';
    mesh(new THREE.PlaneGeometry(canvasW, canvasH), print, .0156).name = 'Unmodified full-resolution portrait';
    scene.add(new THREE.AmbientLight(0xffffff, 1.2));
    const key = new THREE.DirectionalLight(0xffffff, 2.05); key.position.set(-.75, 1.05, 1.15); scene.add(key);
    const fill = new THREE.DirectionalLight(0xfffbf5, .55); fill.position.set(.85, .45, .85); scene.add(fill);

    const requestFrame = () => { if (!disposed && visible && !document.hidden && !frame) frame = requestAnimationFrame(tick); };
    function resize() {
      if (disposed) return;
      const w = Math.ceil(renderer.domElement.getBoundingClientRect().width); if (w < 1) return;
      renderer.setPixelRatio(Math.min(Math.max(devicePixelRatio || 1, 2), 3, 2048 / w));
      renderer.setSize(w, w, false); camera.updateProjectionMatrix(); requestFrame();
    }
    function tick(time) {
      frame = 0; if (disposed || !visible || document.hidden) return;
      const dt = lastTime ? Math.min((time - lastTime) / 1000, .05) : 1 / 60; lastTime = time;
      let tx = 0, ty = 0, tl = 0;
      const max = THREE.MathUtils.degToRad(tilt);
      if (!reduced.matches) {
        if (demoStart !== null) {
          const t = Math.min(1, (time - demoStart) / 2500), envelope = Math.sin(Math.PI * t) ** 2;
          ty = Math.sin(t * Math.PI * 2) * max * envelope; tx = -Math.sin(t * Math.PI) * max * .38 * envelope;
          tl = .006 * motionScale * envelope;
          if (t === 1) { demoStart = null; root.dataset.demo = 'complete'; }
        } else if (pointerInside || hasFocus) {
          // Same desktop response as the approved Collectors 170% setting.
          const mouseMax = pointerInside ? max * 1.44 * 1.70 : max;
          ty = px * mouseMax; tx = py * mouseMax * .62; tl = pointerInside ? .0054 * motionScale : 0;
        }
      }
      const blend = 1 - Math.exp(-dt / inertia);
      rx += (tx - rx) * blend; ry += (ty - ry) * blend; lift += (tl - lift) * blend;
      model.rotation.set(rx, restingYaw + ry, 0, 'YXZ'); model.position.y = lift;
      shadow.style.transform = `scale(${(1 - lift * 4).toFixed(4)},${(1 + lift * 4).toFixed(4)})`;
      shadow.style.opacity = (1 - lift * 13).toFixed(3);
      renderer.render(scene, camera);
      root.dataset.pose = `${(rx * 180 / Math.PI).toFixed(2)},${(ry * 180 / Math.PI).toFixed(2)}`;
      if (demoStart !== null || Math.abs(rx - tx) + Math.abs(ry - ty) + Math.abs(lift - tl) > .00002) requestFrame(); else lastTime = 0;
    }
    stage.addEventListener('pointermove', event => {
      if (event.pointerType === 'touch' || reduced.matches) return;
      const rect = stage.getBoundingClientRect();
      px = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - .5) * 2));
      py = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - .5) * 2));
      pointerInside = true; demoStart = null; requestFrame();
    }, { signal: life.signal, passive: true });
    for (const type of ['pointerleave', 'pointercancel']) stage.addEventListener(type, () => { pointerInside = false; px = py = 0; requestFrame(); }, { signal: life.signal });
    stage.addEventListener('focus', () => { hasFocus = true; }, { signal: life.signal });
    stage.addEventListener('blur', () => { hasFocus = false; px = py = 0; requestFrame(); }, { signal: life.signal });
    stage.addEventListener('keydown', event => {
      if (reduced.matches) return;
      const direction = { ArrowLeft: [-.7, 0], ArrowRight: [.7, 0], ArrowUp: [0, -.7], ArrowDown: [0, .7] }[event.key];
      if (direction) { event.preventDefault(); [px, py] = direction; hasFocus = true; demoStart = null; requestFrame(); }
      if (event.key === 'Escape') { px = py = 0; demoStart = null; requestFrame(); }
    }, { signal: life.signal });
    reduced.addEventListener('change', () => {
      if (reduced.matches) { px = py = rx = ry = lift = 0; demoStart = null; pointerInside = false; }
      requestFrame();
    }, { signal: life.signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && frame) { cancelAnimationFrame(frame); frame = 0; } else { lastTime = 0; requestFrame(); }
    }, { signal: life.signal });
    renderer.domElement.addEventListener('webglcontextlost', event => {
      event.preventDefault(); dispose(); status.textContent = 'Die 3D-Ansicht wurde unterbrochen. Die Produktvorschau bleibt sichtbar.';
    }, { signal: life.signal });
    resizeObserver = new ResizeObserver(resize); resizeObserver.observe(stage);
    visibilityObserver = new IntersectionObserver(entries => {
      const entry = entries[0]; visible = entry.isIntersecting;
      if (!visible && frame) { cancelAnimationFrame(frame); frame = 0; }
      if (visible) {
        lastTime = 0;
        if ((compact.matches || coarse.matches) && !playedMobile && entry.intersectionRatio >= .4 && !reduced.matches) {
          playedMobile = true; demoStart = performance.now(); root.dataset.demo = 'playing';
        }
        requestFrame();
      }
    }, { threshold: [0, .4] });
    visibilityObserver.observe(stage);
    model.rotation.y = restingYaw; resize(); renderer.render(scene, camera);
    root.dataset.ready = 'true'; status.textContent = '';
    window.addEventListener('pagehide', event => { if (!event.persisted) dispose(); }, { signal: life.signal });
    window.addEventListener('pageshow', () => { lastTime = 0; requestFrame(); }, { signal: life.signal });
  } catch (error) {
    dispose(); console.warn('Petrol Portraits Gallery preview:', error);
    status.textContent = 'Die 3D-Ansicht konnte hier nicht gestartet werden. Die Produktvorschau bleibt sichtbar.';
  }
}
if (!customElements.get('pp-gallery-preview')) {
  customElements.define('pp-gallery-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.startObserver?.disconnect();
      this.startObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.startObserver.disconnect(); initializeGallery(this);
      }, { rootMargin: '400px' });
      this.startObserver.observe(this);
    }
    disconnectedCallback() { this.startObserver?.disconnect(); this.disposePreview?.(); }
  });
}
